import { useState } from "react";
import { useClerk, useUser } from "@clerk/react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import { useSession } from "@/context/Session";
import { t } from "@/i18n";

const Row = ({ label, children, testid }) => (
  <div className="grid sm:grid-cols-[10rem_1fr] gap-2 sm:gap-6 py-5 border-t border-[var(--border-color)] items-center" data-testid={testid}>
    <p className="font-medium">{t(label)}</p>
    <div className="min-w-0">{children}</div>
  </div>
);

export default function Profile() {
  const api = useApi();
  const { me, refresh } = useSession();
  const { user } = useUser();
  const { openUserProfile } = useClerk();
  const [nick, setNick] = useState(me.nickname || "");
  const [hide, setHide] = useState(!!me.hide_email);
  const saveNick = async () => {
    try { await api.put("/me/nickname", { nickname: nick }); toast.success(t("Saved")); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleHide = async () => {
    try { await api.put("/me/privacy", { hide_email: !hide }); setHide(!hide); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const orgRow = <Row label="Organization" testid="profile-org-row"><p data-testid="profile-org-name">{me.org?.name}</p>{me.role_names?.length > 0 && <p className="text-xs faint mt-1">{me.role_names.join(" · ")}</p>}</Row>;
  if (me.is_test) return (
    <div className="page stack max-w-[46rem]" data-testid="profile-page">
      <h1 className="text-3xl md:text-4xl font-bold tracking-tight rise">{t("Profile settings")}</h1>
      <div className="card rise d1" data-testid="profile-details-test">
        <p className="text-lg font-semibold pb-4">{t("Profile details")}</p>
        <Row label="User name" testid="profile-username-row"><p data-testid="profile-username">{me.nickname}</p></Row>
        {orgRow}
      </div>
    </div>
  );
  return (
    <div className="page stack max-w-[46rem]" data-testid="profile-page">
      <h1 className="text-3xl md:text-4xl font-bold tracking-tight rise">{t("Profile settings")}</h1>
      <div className="card rise d1" data-testid="profile-details">
        <p className="text-lg font-semibold pb-4">{t("Profile details")}</p>
        <Row label="Profile" testid="profile-row">
          <div className="flex items-center gap-4">
            {user?.imageUrl && <img src={user.imageUrl} alt="" className="w-12 h-12 rounded-full object-cover" />}
            <span className="flex-1 min-w-0 truncate">{user?.fullName || me.name}</span>
            <button className="text-sm text-[var(--accent-primary)] font-semibold hover:opacity-70" onClick={() => openUserProfile()} data-testid="profile-update-button">{t("Update profile")}</button>
          </div>
        </Row>
        <Row label="Nickname" testid="profile-nickname-row">
          <div className="flex gap-2">
            <input className="field !py-2" value={nick} maxLength={30} onChange={(e) => setNick(e.target.value)} data-testid="nickname-input" />
            <button className="btn btn-primary btn-sm" onClick={saveNick} disabled={!nick.trim() || nick === me.nickname} data-testid="nickname-save">{t("Save")}</button>
          </div>
          <p className="text-xs faint mt-2">{t("This is how your team sees you in SMBOI.")}</p>
        </Row>
        <Row label="Email address" testid="profile-email-row">
          <p className="truncate" data-testid="profile-email">{hide ? <span className="faint">{t("Hidden")} ••••••@••••</span> : me.email}</p>
          <label className="flex items-center gap-2 text-sm mt-2 cursor-pointer">
            <input type="checkbox" className="accent-[var(--accent-primary)] w-4 h-4" checked={hide} onChange={toggleHide} data-testid="profile-hide-email" />
            {t("Hide my email from my team")}
          </label>
        </Row>
        {orgRow}
        <Row label="Sign-in & security" testid="profile-security-row">
          <button className="btn btn-ghost btn-sm" onClick={() => openUserProfile()} data-testid="profile-security-button">{t("Manage password and sign-in")}</button>
        </Row>
      </div>
    </div>
  );
}
